import type { ReactElement } from 'react';
import { App, Button, Dropdown } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';

export interface EntityAction {
  key: string;
  label: string;
  href?: string;
  onClick: () => void | Promise<unknown>;
  danger?: boolean;
  disabled?: boolean;
  confirm?: { title: string; description?: string; okText?: string; cancelText?: string };
}

export function EntityActionMenu({
  actions,
  label,
}: {
  actions: EntityAction[];
  label?: string;
}): ReactElement {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const execute = (action: EntityAction): void => {
    if (action.danger || action.confirm) {
      modal.confirm({
        title: action.confirm?.title ?? action.label,
        content: action.confirm?.description,
        okText: action.confirm?.okText ?? t('crm.entity_detail.confirm'),
        cancelText: action.confirm?.cancelText ?? t('crm.entity_detail.cancel'),
        okButtonProps: { danger: action.danger ?? false },
        onOk: action.onClick,
      });
    } else void action.onClick();
  };
  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items: actions.flatMap((action, index) => [
          ...(action.danger && index > 0 && !actions[index - 1]?.danger
            ? [{ key: `${action.key}-divider`, type: 'divider' as const }]
            : []),
          {
            key: action.key,
            label: action.href ? <a href={action.href}>{action.label}</a> : action.label,
            danger: action.danger ?? false,
            disabled: action.disabled ?? false,
            onClick: () => execute(action),
          },
        ]),
      }}
    >
      <Button
        data-testid="entity-action-menu"
        type="text"
        icon={<MoreOutlined />}
        aria-label={label ?? t('crm.entity_detail.more_actions')}
        onClick={(event) => event.stopPropagation()}
      />
    </Dropdown>
  );
}
